import { useEffect, useState, useCallback, useRef } from "react";
import DOMPurify from "dompurify";
import api from "../api";

const MESI = ["", "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const TRIBUTI = [
  ["iva_mensile", "IVA mensile"], ["iva_trimestrale", "IVA trimestrale"], ["ritenute", "Ritenute"],
  ["tassa_libri", "Tassa libri"], ["imu", "IMU"], ["inps", "INPS"],
  ["cciaa", "Diritto CCIAA"], ["unico", "Modello Redditi"],
  ["acconto_iva", "Acconto IVA"], ["altro", "Altro"], ["cred_comp", "Crediti comp."],
];
// formato italiano con punto delle migliaia SEMPRE (it-IT nativo lo omette sotto 10.000)
const _group = (s) => s.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
const eur = (v) => {
  const n = Number(v || 0);
  const [i, d] = Math.abs(n).toFixed(2).split(".");
  return "€ " + (n < 0 ? "-" : "") + _group(i) + "," + d;
};
// "1.234,56" (formato italiano) -> 1234.56 · accetta anche "1234.56" digitato col punto
const parseAmt = (v) => {
  let s = String(v ?? "").replace(/\s/g, "");
  if (s.includes(",")) s = s.replace(/\./g, "").replace(",", ".");
  return parseFloat(s) || 0;
};
// numero -> "1.234,56" (senza decimali se intero)
const fmtNum = (v) => {
  const n = Number(v || 0);
  if (!n) return "";
  const [i, d] = Math.abs(n).toFixed(n % 1 ? 2 : 0).split(".");
  return (n < 0 ? "-" : "") + _group(i) + (d ? "," + d : "");
};
const calcTotal = (amounts) => {
  const debiti = TRIBUTI.filter(([k]) => k !== "cred_comp").reduce((s, [k]) => s + parseAmt(amounts[k]), 0);
  return Math.round((debiti - parseAmt(amounts.cred_comp)) * 100) / 100;
};
const dIT = (s) => (s ? s.split("-").reverse().join("/") : "");

// Colonne visibili per scadenza (giorno/mese). Irap e Imposte redditi tolte:
// tutte le imposte della dichiarazione dei redditi vanno nella cella "Unico".
const COLUMN_RULES = {
  iva_mensile: (d, m) => d === 16 || (d === 20 && m === 8),
  ritenute: (d, m) => d === 16 || (d === 20 && m === 8),
  altro: (d, m) => d === 16 || (d === 20 && (m === 7 || m === 8)),
  cred_comp: (d, m) => d === 16 || (d === 20 && m === 8),
  inps: (d, m) => (d === 16 && [2, 5, 11].includes(m)) || (d === 20 && m === 8),
  tassa_libri: (d, m) => d === 16 && m === 3,
  iva_trimestrale: (d, m) => (d === 16 && [3, 5, 11].includes(m)) || (d === 20 && m === 8),
  cciaa: (d, m) => d === 20 && (m === 7 || m === 8),
  unico: (d, m) => (d === 30 && [6, 7, 11].includes(m)) || (d === 20 && [7, 8].includes(m)) || (d === 16 && [9, 10, 11, 12].includes(m)),
  imu: (d, m) => d === 16 && (m === 6 || m === 12),
  acconto_iva: (d, m) => d === 27 && m === 12,
};

// Se il 16/20/27/30 cade nel weekend la scadenza slitta di 1-2 giorni (es. 18/05):
// riportiamo il giorno al valore "canonico" per applicare le regole delle colonne.
const canonDay = (d) => {
  for (const base of [16, 20, 27, 30]) if (d >= base && d <= base + 2) return base;
  return d;
};

function tributiForDeadline(dueDate, records) {
  if (!dueDate) return TRIBUTI;
  const [, m, dRaw] = dueDate.split("-").map((x) => parseInt(x, 10));
  const d = canonDay(dRaw);
  const withAmount = new Set();
  (records || []).forEach((r) => TRIBUTI.forEach(([k]) => { if (parseAmt(r.amounts?.[k])) withAmount.add(k); }));
  const vis = TRIBUTI.filter(([k]) => (COLUMN_RULES[k] ? COLUMN_RULES[k](d, m) : true) || withAmount.has(k));
  return vis.length ? vis : TRIBUTI;
}
const dShort = (s) => { if (!s) return ""; const [y, m, d] = s.split("-"); return `${d}/${m}/${y.slice(2)}`; };
// Etichetta tab: "Giugno" per le mensili (chiave aaaa-mm), "30 Giu" per le scadenze speciali (chiave aaaa-mm-gg)
const tabLabel = (month) => {
  const p = String(month).split("-");
  const m = parseInt(p[1], 10);
  if (p.length === 3) return `${parseInt(p[2], 10)} ${MESI[m].slice(0, 3)}`;
  return MESI[m];
};

function AuthBadge({ v }) {
  const map = {
    autorizzato: ["Autorizzato", "#1f9d55"], rinviato: ["Rinviato", "#d97706"],
    in_attesa: ["In attesa", "#0ea5e9"],
  };
  const [label, color] = map[v] || ["—", "#5e6e92"];
  return <span style={{ color, fontWeight: 800, fontSize: 12.5 }}>{label}</span>;
}

export default function F24() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [deadlines, setDeadlines] = useState([]);
  const [sel, setSel] = useState(null);
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState({});
  const [companies, setCompanies] = useState([]);
  const [persons, setPersons] = useState([]);
  const [edit, setEdit] = useState(null);
  const [draft, setDraft] = useState(null);
  const [remind, setRemind] = useState(false);
  const [smsModal, setSmsModal] = useState(false);
  const [preview, setPreview] = useState(null);
  const [vociRec, setVociRec] = useState(null);
  const [storiaRec, setStoriaRec] = useState(null);
  const [recurModal, setRecurModal] = useState(false);
  const [newDl, setNewDl] = useState(false);
  const [missing, setMissing] = useState([]);
  const [redditiRows, setRedditiRows] = useState(null);
  const [msg, setMsg] = useState("");
  const [importReport, setImportReport] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [helpModal, setHelpModal] = useState(false);
  const [showAllClients, setShowAllClients] = useState(() => localStorage.getItem("f24_show_all") === "1");
  const [markMode, setMarkMode] = useState(false);

  const loadDeadlines = useCallback(() => {
    api.get(`/f24/deadlines?year=${year}`).then((r) => {
      setDeadlines(r.data);
      // all'apertura seleziona la prossima scadenza (la prima non ancora passata)
      const today = new Date().toISOString().slice(0, 10);
      const next = r.data.find((d) => (d.due_date || `${d.month}-28`) >= today) || r.data[r.data.length - 1];
      setSel((cur) => cur || (next && next.id) || null);
    });
    api.get(`/f24/stats?year=${year}`).then((r) => setStats(r.data));
  }, [year]);

  const changeYear = (y) => { setSel(null); setYear(y); };

  const deleteDeadline = async () => {
    const d = deadlines.find((x) => x.id === sel);
    if (!d) return;
    const n = records.filter((r) => !r.no_deadline).length;
    if (!window.confirm(`Eliminare la scadenza "${d.label || ""} ${dShort(d.due_date)}"?\n⚠️ Verranno cancellati anche i suoi ${n} record cliente (importi, autorizzazioni…). L'operazione non si può annullare.`)) return;
    if (n > 5 && !window.confirm(`Questa scadenza ha ${n} clienti con dati. Confermi DAVVERO l'eliminazione?`)) return;
    try {
      await api.delete(`/f24/deadlines/${sel}`);
      setSel(null);
      await loadDeadlines();
      flash("Scadenza eliminata.");
    } catch (e) { flash("Eliminazione non riuscita: " + (e.response?.data?.detail || e.message)); }
  };

  const selRef = useRef(null);
  useEffect(() => { selRef.current = sel; }, [sel]);

  // la scadenza selezionata (di default la prossima) resta sempre al centro della barra orizzontale
  const tabsRef = useRef(null);
  useEffect(() => {
    const c = tabsRef.current;
    if (!c || !sel) return;
    const btn = c.querySelector("button.active");
    if (btn) c.scrollTo({ left: btn.offsetLeft - (c.clientWidth - btn.offsetWidth) / 2, behavior: "smooth" });
  }, [sel, deadlines]);
  const scrollTabs = (dir) => {
    const c = tabsRef.current;
    if (c) c.scrollBy({ left: dir * Math.max(220, c.clientWidth * 0.6), behavior: "smooth" });
  };

  const loadRecords = useCallback(() => {
    if (!sel) { setRecords([]); setMissing([]); return; }
    const cur = sel;
    // Guardia anti-race: se nel frattempo l'utente ha cambiato scadenza, ignora la risposta vecchia
    api.get(`/f24/deadlines/${cur}/records`).then((r) => { if (selRef.current === cur) setRecords(r.data); });
    api.get(`/f24/deadlines/${cur}/missing-recurring`)
      .then((r) => { if (selRef.current === cur) setMissing(r.data); })
      .catch(() => { if (selRef.current === cur) setMissing([]); });
  }, [sel]);

  // Cambiando scadenza svuota subito la tabella (evita di vedere i dati della scadenza precedente)
  useEffect(() => { setRecords([]); setMissing([]); }, [sel]);

  useEffect(() => {
    loadDeadlines();
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    api.get("/persons").then((r) => setPersons(r.data)).catch(() => {});
  }, [loadDeadlines]);
  useEffect(() => { loadRecords(); }, [loadRecords]);

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(""), 4000); };

  const expectedOf = (cid) => (companies.find((c) => c.id === cid)?.f24_expected_tributes) || [];
  const IVA_KEYS = ["iva_mensile", "iva_trimestrale"];
  const toggleExpected = async (cid, k) => {
    if (!cid) { flash("Abbina prima il cliente all'anagrafica per poter segnare i tributi attesi."); return; }
    if (!IVA_KEYS.includes(k)) { flash("L'evidenziazione automatica vale solo per l'IVA. Per gli altri tributi usa 👥 Clienti ricorrenti."); return; }
    const now = expectedOf(cid).includes(k);
    setCompanies((prev) => prev.map((c) => c.id === cid
      ? { ...c, f24_expected_tributes: now ? (c.f24_expected_tributes || []).filter((x) => x !== k) : [...(c.f24_expected_tributes || []), k] }
      : c));
    try { await api.post("/f24/expected", { company_id: cid, tribute: k, expected: !now }); loadRecords(); }
    catch (e) { flash("Errore: " + (e.response?.data?.detail || e.message)); }
  };
  const cellHl = (cid, k, amounts, zeroFilled) => {
    if (!cid || parseAmt(amounts?.[k]) > 0 || (zeroFilled || []).includes(k)) return undefined;
    const marked = IVA_KEYS.includes(k) && expectedOf(cid).includes(k);
    if (!marked && !missingSet.has(`${cid}:${k}`)) return undefined;
    return { background: "rgba(250, 204, 21, .16)", border: "1px solid rgba(250, 204, 21, .55)" };
  };
  // ricorrenti/attesi senza importo in questa scadenza → celle evidenziate
  const missingSet = new Set((missing || []).map((m) => `${m.company_id}:${m.tributo}`));
  const missingClientIds = new Set((missing || []).map((m) => m.company_id).filter(Boolean));
  const createFromPlaceholder = async (c, k, val) => {
    const raw = String(val ?? "").trim();
    const num = parseAmt(val);
    const isZeroMark = raw !== "" && num === 0;
    if (!num && !isZeroMark) return;
    try {
      await api.post(`/f24/deadlines/${sel}/records`, {
        company_id: c.id, client_name: c.name, client_email: c.email || null,
        amounts: { [k]: num }, zero_filled: isZeroMark ? [k] : [],
      });
      loadRecords(); loadDeadlines();
    } catch (e) { flash("Errore: " + (e.response?.data?.detail || e.message)); }
  };
  // clienti senza riga in questa scadenza: tutti (se richiesto) oppure solo quelli con celle evidenziate
  const placeholderClients = companies
    .filter((c) => c.kind === "cliente" && !records.some((r) => r.company_id === c.id))
    .filter((c) => showAllClients || missingClientIds.has(c.id))
    .sort((a, b) => a.name.localeCompare(b.name));

  const generate = async () => { await api.post(`/f24/deadlines/generate?year=${year}`); loadDeadlines(); };

  const createDeadline = async ({ due_date, label }) => {
    const y = parseInt(due_date.slice(0, 4), 10);
    const m = parseInt(due_date.slice(5, 7), 10);
    const r = await api.post(`/f24/deadlines`, { year: y, month: m, due_date, label });
    setNewDl(false);
    setSel(r.data.id);
    if (y !== year) setYear(y);
    else await loadDeadlines();
  };


  const exportData = async () => {
    try {
      const r = await api.get(`/f24/export-data`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = u; a.download = "f24_export.json";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(u), 2000);
      flash("Dati F24 esportati. Ora caricali nell'altro ambiente con 'Importa dati'.");
    } catch (e) { flash("Errore export: " + (e.response?.data?.detail || e.message)); }
  };

  const importData = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!window.confirm("Attenzione: questo SOSTITUISCE tutte le scadenze e i record F24 di questo ambiente con quelli del file. Continuare?")) { e.target.value = ""; return; }
    setImporting(true);
    flash("Importo i dati F24…");
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/f24/import-data`, fd);
      loadDeadlines(); loadRecords();
      flash(`Dati importati: ${r.data.deadlines_imported} scadenze, ${r.data.records_imported} record.`);
    } catch (err) { flash("Errore import dati: " + (err.response?.data?.detail || err.message)); }
    setImporting(false);
    e.target.value = "";
  };

  const doImportScadenze = async (pdfFile, excelFile) => {
    setImporting(true);
    flash("Importo le scadenze… attendi qualche secondo.");
    try {
      const fd = new FormData();
      fd.append("pdf_file", pdfFile);
      fd.append("excel_file", excelFile);
      const r = await api.post(`/f24/import-scadenze`, fd);
      setImportReport(r.data);
      setImportModal(false);
      loadDeadlines(); loadRecords();
      flash(`Import completato: ${r.data.created} nuovi, ${r.data.updated} aggiornati.`);
    } catch (err) { flash("Errore import: " + (err.response?.data?.detail || err.message)); }
    setImporting(false);
  };

  const onImport = async (e) => {
    const file = e.target.files[0];
    if (!file || !sel) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/f24/deadlines/${sel}/import`, fd);
      flash(`Import: ${r.data.created} nuovi, ${r.data.updated} aggiornati, ${r.data.matched_clients} clienti abbinati.`);
      loadRecords(); loadDeadlines();
    } catch (err) { flash("Errore import: " + (err.response?.data?.detail || err.message)); }
    e.target.value = "";
  };

  const onRedditiPdf = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!sel) { flash("Seleziona prima una scadenza (tab in alto), poi carica i PDF."); e.target.value = ""; return; }
    e.target.value = "";
    // Upload a PEZZI + parsing in background con polling: nessuna richiesta lunga
    // → niente errori Cloudflare per timeout/limiti del proxy in produzione.
    const jobId = "j" + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
    const CHUNK = 1.5 * 1024 * 1024;
    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const total = Math.max(1, Math.ceil(f.size / CHUNK));
        for (let c = 0; c < total; c++) {
          setMsg(`Carico ${f.name} (${i + 1}/${files.length})${total > 1 ? ` — parte ${c + 1}/${total}` : ""}…`);
          const fd = new FormData();
          fd.append("job_id", jobId);
          fd.append("filename", f.name);
          fd.append("index", String(c));
          fd.append("total", String(total));
          fd.append("chunk", f.slice(c * CHUNK, (c + 1) * CHUNK), f.name);
          await api.post(`/f24/redditi-upload-chunk`, fd, { timeout: 60000 });
        }
      }
      await api.post(`/f24/redditi-parse-start`, { job_id: jobId, files: files.map((f) => f.name) });
      let st = null;
      let failures = 0;
      for (let t = 0; t < 120; t++) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          st = (await api.get(`/f24/redditi-parse-status/${jobId}`, { noCache: true })).data;
          failures = 0;
        } catch (_) {
          failures += 1;
          if (failures >= 10) { st = { status: "errore", error: "il server non risponde. Attendi qualche minuto e riprova: i tuoi dati non sono stati toccati." }; break; }
          continue;
        }
        setMsg(`Leggo i PDF… ${st.done}/${st.total}${st.current ? ` (${st.current})` : ""}`);
        if (st.status !== "in_corso") break;
      }
      if (st?.status === "in_corso") st = { status: "errore", error: "lettura troppo lenta: riprova o carica un PDF con meno pagine." };
      setMsg("");
      const rows = st?.results || [];
      if (st?.status === "errore") flash("Errore lettura PDF: " + (st.error || "sconosciuto"));
      else if (rows.length) setRedditiRows(rows);
      else flash("Nessun dato leggibile dai PDF selezionati.");
    } catch (err) {
      setMsg("");
      flash("Errore caricamento: " + (err.response?.data?.detail || err.message));
    }
  };

  const createRedditiRecords = async (rows) => {
    const r = await api.post(`/f24/import-redditi-confirm`, {
      rows: rows.map((x) => ({
        client_name: x.client_name, client_email: x.client_email || null,
        company_id: x.company_id || null, amounts: x.amounts, rate: x.rate || [],
        second_acconto: x.second_acconto || null,
      })),
      fallback_deadline_id: sel,
    });
    setRedditiRows(null);
    loadRecords(); loadDeadlines();
    flash(`Create ${r.data.rows} righe su ${r.data.deadlines} scadenze dai PDF Redditi.`);
  };

  const patch = async (id, body) => {
    await api.put(`/f24/records/${id}`, body);
    loadRecords(); loadDeadlines();
  };

  const origAmt = useRef({});

  const updateAmount = (id, k, val) => {
    setRecords((prev) => prev.map((row) => row.id === id ? { ...row, amounts: { ...row.amounts, [k]: val } } : row));
  };

  const saveAmount = async (r, k, val) => {
    const raw = String(val ?? "").trim();
    const num = parseAmt(val);
    const isZeroMark = raw !== "" && num === 0; // "0" scritto esplicitamente = liquidato (es. IVA a credito)
    const zf = new Set(r.zero_filled || []);
    const hadZero = zf.has(k);
    const orig = origAmt.current[`${r.id}:${k}`];
    if (orig !== undefined && num === parseAmt(orig) && isZeroMark === hadZero) return; // davvero invariato
    const amounts = {};
    TRIBUTI.forEach(([kk]) => { amounts[kk] = parseAmt(r.amounts[kk]); });
    amounts[k] = num;
    if (isZeroMark) zf.add(k); else zf.delete(k);
    const zeroArr = [...zf];
    setRecords((prev) => prev.map((row) => row.id === r.id ? { ...row, amounts, zero_filled: zeroArr, totale: calcTotal(amounts) } : row));
    try { await api.put(`/f24/records/${r.id}`, { amounts, zero_filled: zeroArr }); loadDeadlines(); }
    catch (e) { flash("⚠️ Importo NON salvato: " + (e.response?.data?.detail || e.message)); }
  };

  // valore mostrato nella cella: formattato it-IT se salvato, testo grezzo mentre si digita
  const cellVal = (r, k) => {
    const v = r.amounts[k];
    if (typeof v === "string") return v;
    if (v) return fmtNum(v);
    return (r.zero_filled || []).includes(k) ? "0" : "";
  };
  const exportCsv = () => {
    const headers = ["Cliente", ...TRIBUTI.map(([, l]) => l), "Totale", "Avviso", "Autorizzazione", "Invio F24", "Inviato cliente", "Pagato", "Rifiutato banca", "Nessuna scadenza"];
    const rows = records.map((r) => [
      r.client_name,
      ...TRIBUTI.map(([k]) => String(r.amounts[k] || 0).replace(".", ",")),
      String(r.totale || 0).replace(".", ","),
      r.notice_sent_at ? "inviato" : "", r.authorization || "",
      r.f24_sent ? "sì" : "", r.client_sent ? "sì" : "", r.paid ? "sì" : "",
      r.bank_rejected ? "sì" : "", r.no_deadline ? "sì" : "",
    ]);
    const csv = [headers, ...rows].map((row) => row.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `F24_${current ? tabLabel(current.month) : "export"}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const current = deadlines.find((d) => d.id === sel);
  const visTributi = tributiForDeadline(current?.due_date, records);

  return (
    <div className="f24-fullwidth" data-testid="f24-page">
      <h1 className="page-title">Scadenze F24</h1>
      <p className="page-sub">Importi da versare per cliente, autorizzazioni e solleciti.{" "}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <button className="btn sm" style={{ padding: "0 7px" }} onClick={() => changeYear(year - 1)} title="Anno precedente" data-testid="f24-year-prev">◀</button>
          <b data-testid="f24-year">{year}</b>
          <button className="btn sm" style={{ padding: "0 7px" }} onClick={() => changeYear(year + 1)} title="Anno successivo" data-testid="f24-year-next">▶</button>
        </span>{" "}
        <span className="muted" style={{ fontSize: 11 }} data-testid="f24-version">· import v2</span></p>

      <div className="toolbar" style={{ marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <button className="btn primary" onClick={() => setImportModal(true)} data-testid="f24-import-scadenze-btn">
          📊 Importa scadenze (PDF + Excel)
        </button>
        <button className="btn" onClick={exportData} data-testid="f24-export-data-btn">⬇ Esporta dati F24</button>
        <label className="btn" style={{ cursor: "pointer" }} data-testid="f24-import-data-btn">
          ⬆ Importa dati F24 (JSON)
          <input type="file" accept=".json" onChange={importData} style={{ display: "none" }} />
        </label>
        <button className="btn" onClick={() => setHelpModal(true)} data-testid="f24-help-btn">❔ Istruzioni</button>
        <span className="muted" style={{ fontSize: 12.5, flexBasis: "100%" }}>
          PDF "Scadenze" = importi · Excel esiti = versato/non versato. Per copiare i dati tra sito reale e preview: "Esporta" da un ambiente e "Importa" nell'altro.
        </span>
      </div>

      {msg && <div className="panel" style={{ borderColor: "#0ea5e9" }} data-testid="f24-flash">{msg}</div>}

      {deadlines.length === 0 ? (
        <div className="panel">
          <p>Nessuna scadenza per il {year}.</p>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn primary" onClick={generate} data-testid="f24-generate-btn">Genera scadenze {year}</button>
            <button className="btn" onClick={() => setNewDl(true)} data-testid="f24-add-deadline-empty">+ Scadenza personalizzata</button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16 }}>
            <button className="tabs-arrow" onClick={() => scrollTabs(-1)} data-testid="f24-tabs-prev" title="Scadenze precedenti">◀</button>
            <div ref={tabsRef} className="tabs tabs-scroll" style={{ marginBottom: 0, display: "flex", flex: 1, minWidth: 0 }} data-testid="f24-month-tabs">
              {deadlines.map((d) => (
                <button key={d.id} className={d.id === sel ? "active" : ""} onClick={() => setSel(d.id)}
                  data-testid={`f24-month-${d.month}`} title={d.label}>
                  {dShort(d.due_date)}
                  {d.records_count > 0 && <span style={{ opacity: .6, marginLeft: 6 }}>({d.records_count})</span>}
                </button>
              ))}
              <button onClick={() => setNewDl(true)} data-testid="f24-add-deadline-btn" title="Aggiungi scadenza (es. Redditi 20/07)">+ scadenza</button>
            </div>
            <button className="tabs-arrow" onClick={() => scrollTabs(1)} data-testid="f24-tabs-next" title="Scadenze successive">▶</button>
          </div>

          {current && (
            <div className="panel" style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center" }}>
              <div><strong style={{ fontSize: 18 }}>Scadenza: {dIT(current.due_date)}</strong></div>
              <div className="muted">Clienti: <b>{current.records_count}</b></div>
              <div className="muted">Avvisi inviati: <b>{current.notice_sent_count}</b></div>
              <div className="muted">Autorizzati: <b style={{ color: "#6ee7b7" }}>{current.authorized_count}</b></div>
              <div className="muted">Totale mese: <b>{eur(current.total_amount)}</b></div>
              <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
                <label className="btn sm" style={{ cursor: "pointer" }} data-testid="f24-import-btn">
                  Importa importi (Excel/CSV)
                  <input type="file" accept=".xlsx,.xls,.csv" onChange={onImport} style={{ display: "none" }} />
                </label>
                <label className="btn sm" style={{ cursor: "pointer" }} data-testid="f24-redditi-pdf-btn" title="Carica una o più dichiarazioni Redditi (PDF): precompila importi e cliente">
                  📄 Carica PDF Redditi
                  <input type="file" accept=".pdf" multiple onChange={onRedditiPdf} style={{ display: "none" }} />
                </label>
                <button className="btn sm" onClick={exportCsv} data-testid="f24-export-btn">⬇ Esporta tabella</button>
                <button className="btn sm" onClick={() => setEdit({ deadline_id: sel, amounts: {}, client_name: "", _new: true })}
                  data-testid="f24-add-row-btn">+ Aggiungi cliente</button>
                <button className="btn sm" onClick={() => setRemind(true)} data-testid="f24-reminder-config-btn">⏰ Promemoria automatici</button>
                <button className="btn sm" onClick={() => setSmsModal(true)} data-testid="f24-sms-sollecito-btn" title="Invia un SMS ai clienti che hanno ricevuto l'avviso ma non hanno ancora risposto">📱 SMS solleciti</button>
                <button className="btn sm" onClick={() => setRecurModal(true)} data-testid="f24-recurring-btn">👥 Clienti ricorrenti</button>
              </div>
            </div>
          )}

          {missing.length > 0 && (
            <div className="muted" style={{ fontSize: 12, margin: "6px 0" }} data-testid="f24-missing-hint">
              🖌 {missing.length} celle evidenziate in giallo: importi attesi (ricorrenti o marcati) non ancora inseriti. Scrivi l'importo nella cella per completare — vale anche <b>0</b> (es. IVA liquidata a credito).
            </div>
          )}

          <div className="panel f24-table-wrap">
            {records.length > 0 && (() => {
              const c = { aut: 0, rin: 0, att: 0, senza: 0, pagati: 0 };
              records.forEach((r) => {
                if (r.paid) { c.pagati += 1; return; }
                if (r.authorization === "autorizzato") c.aut += 1;
                else if (r.authorization === "rinviato") c.rin += 1;
                else if (r.authorization === "in_attesa") c.att += 1;
                else c.senza += 1;
              });
              return (
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 13, padding: "2px 0 8px", fontWeight: 600 }} data-testid="f24-auth-summary">
                  <span style={{ color: "#6ee7b7" }} data-testid="f24-sum-autorizzati">✓ {c.aut} autorizzati</span>
                  <span style={{ color: "#fbbf24" }} data-testid="f24-sum-rinviati">⏳ {c.rin} rinviati</span>
                  <span style={{ color: "#93c5fd" }} data-testid="f24-sum-attesa">⏱ {c.att} in attesa di risposta</span>
                  <span className="muted" data-testid="f24-sum-senza">— {c.senza} senza avviso</span>
                  {c.pagati > 0 && <span style={{ color: "#a7f3d0" }} data-testid="f24-sum-pagati">💰 {c.pagati} pagati</span>}
                </div>
              );
            })()}
            <div className="toolbar" style={{ marginTop: 0, gap: 8, flexWrap: "wrap" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                <input type="checkbox" checked={showAllClients} onChange={(e) => { setShowAllClients(e.target.checked); localStorage.setItem("f24_show_all", e.target.checked ? "1" : "0"); }} data-testid="f24-show-all" />
                Mostra tutti i clienti (anche senza importi)
              </label>
              <button className={"btn sm" + (markMode ? " primary" : "")} onClick={() => setMarkMode((v) => !v)}
                title="Attiva e clicca sulle celle IVA per segnare i clienti con IVA attesa ogni mese: restano evidenziate in giallo finché non inserisci l'importo. Per gli altri tributi usa Clienti ricorrenti"
                data-testid="f24-mark-mode">🖌 {markMode ? "Fine evidenziazione" : "Evidenzia attesi"}</button>
              {markMode && <span className="muted" style={{ fontSize: 12 }}>Clicca sulle celle <b>IVA</b> da marcare come attese (di nuovo per togliere). Per gli altri tributi (Redditi, IMU…) usa 👥 Clienti ricorrenti.</span>}
              <div className="spacer" />
              <button className="btn sm" onClick={deleteDeadline} title="Elimina questa scadenza e tutti i suoi dati (es. doppioni creati per errore)" data-testid="f24-del-deadline">🗑 Elimina scadenza</button>
            </div>
            <table className="f24-compact" data-testid="f24-records-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  {visTributi.map(([k, l]) => <th key={k} style={{ textAlign: "right" }}>{l}</th>)}
                  <th style={{ textAlign: "right" }}>Totale</th>
                  <th>Avviso</th><th>Autorizz.</th><th>Invio F24</th><th>Inviato cl.</th><th>Pagato</th><th>Rifiut. banca</th><th>Nessuna scad.</th><th>Ravved.</th><th></th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => {
                  const rinviatoTot = r.authorization === "rinviato" && !r.paid;
                  const rinviatoParz = !rinviatoTot && !r.paid && Object.values(r.item_status || {}).includes("rinviato");
                  const autorizzato = r.authorization === "autorizzato";
                  const rowStyle = r.no_deadline ? { opacity: 0.45 }
                    : (rinviatoTot || rinviatoParz) ? { background: "rgba(239,68,68,.14)", boxShadow: "inset 4px 0 0 #ef4444" } : undefined;
                  return (
                  <tr key={r.id} data-testid={`f24-row-${r.id}`} style={rowStyle}>
                    <td><b style={autorizzato ? { color: "#22c55e" } : undefined}>
                      {autorizzato && <span style={{ color: "#22c55e", fontWeight: 900, fontSize: 16, marginRight: 6 }} title="Il cliente ha autorizzato il pagamento" data-testid={`f24-autorizzato-${r.id}`}>✔</span>}
                      {r.client_name}</b>
                      {rinviatoTot && <div style={{ color: "#ef4444", fontSize: 11.5, fontWeight: 900, letterSpacing: 0.5 }} data-testid={`f24-rinviato-${r.id}`}>⛔ RINVIATO — NON INVIARE</div>}
                      {rinviatoParz && <div style={{ color: "#ef4444", fontSize: 11.5, fontWeight: 900, letterSpacing: 0.5 }} data-testid={`f24-rinviato-${r.id}`}>⛔ RINVIO PARZIALE — vedi voci</div>}
                      {!r.no_deadline && !r.paid && !r.client_email && <div style={{ color: "#fb7185", fontSize: 11 }}>email mancante</div>}{r.no_deadline && <div style={{ color: "#94a3b8", fontSize: 11 }}>nessuna scadenza</div>}</td>
                    {visTributi.map(([k]) => (
                      <td key={k} style={{ textAlign: "right", padding: "2px 4px", ...(markMode && r.company_id ? { cursor: "pointer" } : {}) }}
                        onClick={markMode ? () => toggleExpected(r.company_id, k) : undefined}>
                        <input value={cellVal(r, k)}
                          onChange={(e) => updateAmount(r.id, k, e.target.value)}
                          onFocus={(e) => { origAmt.current[`${r.id}:${k}`] = e.target.value; }}
                          onBlur={(e) => saveAmount(r, k, e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                          placeholder="—" data-testid={`f24-amt-${r.id}-${k}`} readOnly={markMode}
                          style={{ width: 64, textAlign: "right", padding: "3px 5px", fontSize: 11.5, ...(markMode ? { pointerEvents: "none" } : {}), ...cellHl(r.company_id, k, r.amounts, r.zero_filled) }} />
                      </td>
                    ))}
                    <td style={{ textAlign: "right", fontWeight: 800 }} data-testid={`f24-total-${r.id}`}>{eur(calcTotal(r.amounts))}</td>
                    <td>{r.notice_sent_at ? <span style={{ color: "#6ee7b7", fontSize: 12 }}>✓ inviato</span> : <span className="muted" style={{ fontSize: 12 }}>—</span>}</td>
                    <td>
                      <select value={r.authorization || ""} onChange={(e) => patch(r.id, { authorization: e.target.value })}
                        style={{ padding: "4px 6px", fontSize: 12 }} data-testid={`f24-auth-${r.id}`}>
                        <option value="">—</option>
                        <option value="in_attesa">In attesa</option>
                        <option value="autorizzato">Autorizzato</option>
                        <option value="rinviato">Rinviato</option>
                      </select>
                      <div>
                        <button className="btn sm" style={{ fontSize: 10.5, padding: "1px 6px", marginTop: 3 }}
                          onClick={() => setVociRec({ ...r })} data-testid={`f24-voci-${r.id}`}
                          title="Risposta mista del cliente: gestisci ogni voce (autorizzata / rinviata / versata)">
                          {vociSummary(r) || "per voce…"}
                        </button>
                      </div>
                    </td>
                    <td style={{ textAlign: "center" }}><input type="checkbox" checked={!!r.f24_sent} onChange={(e) => patch(r.id, { f24_sent: e.target.checked })} data-testid={`f24-sent-${r.id}`} /></td>
                    <td style={{ textAlign: "center" }}><input type="checkbox" checked={!!r.client_sent} onChange={(e) => patch(r.id, { client_sent: e.target.checked })} data-testid={`f24-client-sent-${r.id}`} /></td>
                    <td style={{ textAlign: "center" }}><input type="checkbox" checked={!!r.paid} onChange={(e) => patch(r.id, { paid: e.target.checked })} data-testid={`f24-paid-${r.id}`} /></td>
                    <td style={{ textAlign: "center" }} title="Se la banca rifiuta il pagamento: torna tra i non pagati da sollecitare"><input type="checkbox" checked={!!r.bank_rejected} onChange={(e) => patch(r.id, { bank_rejected: e.target.checked })} data-testid={`f24-bank-rejected-${r.id}`} /></td>
                    <td style={{ textAlign: "center" }} title="Il cliente non ha scadenze F24 questo mese"><input type="checkbox" checked={!!r.no_deadline} onChange={(e) => patch(r.id, { no_deadline: e.target.checked })} data-testid={`f24-no-deadline-${r.id}`} /></td>
                    <td style={{ fontSize: 11, maxWidth: 110 }}>{(r.ravvedimento_dates || []).map(dIT).join(", ") || "—"}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button className="btn sm" onClick={() => setEdit({ ...r })} data-testid={`f24-edit-${r.id}`}>Modifica</button>{" "}
                      <button className="btn sm" onClick={() => setStoriaRec(r)} data-testid={`f24-storia-${r.id}`} title="Storia: email e SMS inviati, risposte del cliente">📜</button>{" "}
                      {!r.no_deadline && <button className="btn sm" onClick={() => setDraft({ record: r, kind: "notice" })} data-testid={`f24-notice-${r.id}`}>Avviso</button>}{" "}
                      {!r.no_deadline && !r.paid && <button className="btn sm danger" onClick={() => setPreview(r)} data-testid={`f24-sollecito-${r.id}`}>Sollecito</button>}
                    </td>
                  </tr>
                  );
                })}
                {placeholderClients.map((c) => (
                  <tr key={`ph-${c.id}`} data-testid={`f24-ph-row-${c.id}`} style={{ opacity: 0.85 }}>
                    <td><b style={{ fontWeight: 600, color: "var(--muted)" }}>{c.name}</b></td>
                    {visTributi.map(([k]) => (
                      <td key={k} style={{ textAlign: "right", padding: "2px 4px", ...(markMode ? { cursor: "pointer" } : {}) }}
                        onClick={markMode ? () => toggleExpected(c.id, k) : undefined}>
                        <input defaultValue=""
                          onBlur={(e) => createFromPlaceholder(c, k, e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                          placeholder="—" data-testid={`f24-ph-amt-${c.id}-${k}`} readOnly={markMode}
                          style={{ width: 64, textAlign: "right", padding: "3px 5px", fontSize: 11.5, ...(markMode ? { pointerEvents: "none" } : {}), ...cellHl(c.id, k, {}) }} />
                      </td>
                    ))}
                    <td style={{ textAlign: "right", color: "var(--muted)" }}>—</td>
                    <td colSpan={7} className="muted" style={{ fontSize: 11 }}>nessun F24 in questa scadenza — inserisci un importo per crearlo</td>
                  </tr>
                ))}
                {records.length === 0 && placeholderClients.length === 0 && <tr><td colSpan={visTributi.length + 9} className="muted" style={{ textAlign: "center", padding: 30 }}>Nessun cliente. Importa un file o aggiungi manualmente.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {edit && <EditModal rec={edit} companies={companies} persons={persons} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); loadRecords(); loadDeadlines(); }} />}
      {vociRec && <VociModal rec={vociRec} onClose={() => setVociRec(null)} onSaved={() => { setVociRec(null); loadRecords(); flash("Stato delle voci salvato: le voci 💰 versate e 📅 ravvedute non finiranno nei solleciti."); }} />}
      {storiaRec && <StoriaModal rec={storiaRec} onClose={() => setStoriaRec(null)} />}
      {draft && <DraftModal data={draft} onClose={() => setDraft(null)} onSent={() => { setDraft(null); loadRecords(); loadDeadlines(); flash("Email inviata."); }} onSaved={() => { setDraft(null); flash("📝 Bozza salvata: la ritrovi riaprendo Avviso/Sollecito di questo cliente."); }} />}
      {remind && <ReminderModal month={current ? current.month : null} onClose={() => setRemind(false)} onDone={(m) => { loadRecords(); loadDeadlines(); flash(m); }} />}
      {smsModal && <SmsSollecitoModal deadlineId={sel} onClose={() => setSmsModal(false)} onDone={(m) => { setSmsModal(false); loadRecords(); flash(m); }} />}
      {preview && <ReminderPreviewModal record={preview} onClose={() => setPreview(null)} onSent={(m) => { setPreview(null); loadRecords(); loadDeadlines(); flash(m); }} />}
      {recurModal && <RecurringModal companies={companies} onClose={() => setRecurModal(false)} onChanged={() => loadRecords()} />}
      {newDl && <NewDeadlineModal onClose={() => setNewDl(false)} onSave={createDeadline} />}
      {redditiRows && <RedditiImportModal rows={redditiRows} deadlineLabel={current ? (current.label && !current.label.startsWith("F24 ") ? current.label : tabLabel(current.month)) : ""} onClose={() => setRedditiRows(null)} onConfirm={createRedditiRecords} />}
      {importModal && <ImportScadenzeModal importing={importing} onClose={() => setImportModal(false)} onImport={doImportScadenze} />}
      {helpModal && <ImportHelpModal onClose={() => setHelpModal(false)} />}
      {importReport && <ImportReportModal report={importReport} onClose={() => setImportReport(null)} />}
    </div>
  );
}

// riassunto compatto dello stato per voce (es. "✓1 ⏳1 💰1")
const vociSummary = (r) => {
  const ist = r.item_status || {};
  const c = { autorizzato: 0, rinviato: 0, versato: 0, ravveduto: 0 };
  Object.values(ist).forEach((v) => { if (c[v] !== undefined) c[v] += 1; });
  const parts = [];
  if (c.autorizzato) parts.push(`✓${c.autorizzato}`);
  if (c.rinviato) parts.push(`⏳${c.rinviato}`);
  if (c.versato) parts.push(`💰${c.versato}`);
  if (c.ravveduto) parts.push(`📅${c.ravveduto}`);
  return parts.join(" ");
};

const LOG_LABELS = {
  avviso_inviato: ["📧", "Avviso email inviato"],
  sollecito_inviato: ["📧", "Sollecito email inviato"],
  sollecito_in_coda: ["⏳", "Sollecito email in coda"],
  sollecito_auto_in_coda: ["⏰", "Sollecito automatico in coda"],
  sollecito_errore: ["⚠️", "Errore invio sollecito"],
  sms_sollecito: ["📱", "SMS di sollecito inviato"],
  feedback_cliente: ["🖱", "Risposta del cliente dall'email"],
  autorizzazione_manuale: ["✍️", "Autorizzazione modificata a mano"],
  invio_f24: ["🏦", "Invio F24"],
  f24_inviato_cliente: ["📤", "F24 inviato al cliente"],
  pagamento: ["💰", "Pagamento"],
  rifiutato_banca: ["🚫", "Rifiutato dalla banca"],
  nessuna_scadenza: ["—", "Nessuna scadenza"],
  ravvedimento: ["📅", "Ravvedimento"],
};

function StoriaModal({ rec, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/f24/records/${rec.id}/log`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [rec.id]);
  const dt = (s) => (s ? new Date(s).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "");
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 620 }} data-testid="f24-storia-modal">
        <h3>📜 Storia — {rec.client_name}</h3>
        <p className="muted" style={{ fontSize: 12.5 }}>Tutte le email e gli SMS inviati per questo F24, con le risposte del cliente.</p>
        {rows === null ? <p className="muted">Carico…</p> : rows.length === 0 ? (
          <p className="muted" data-testid="f24-storia-empty">Ancora nessuna azione registrata per questa riga.</p>
        ) : (
          <div style={{ maxHeight: 380, overflowY: "auto" }}>
            {rows.map((l) => {
              const [icon, label] = LOG_LABELS[l.action] || ["•", l.action];
              return (
                <div key={l.id} style={{ display: "flex", gap: 10, padding: "7px 2px", borderBottom: "1px solid rgba(148,163,184,.15)", fontSize: 13 }} data-testid={`f24-storia-item-${l.id}`}>
                  <span style={{ width: 22, textAlign: "center" }}>{icon}</span>
                  <div style={{ flex: 1 }}>
                    <b>{label}</b>
                    {l.detail && <span className="muted"> — {l.detail}</span>}
                  </div>
                  <span className="muted" style={{ whiteSpace: "nowrap" }}>{dt(l.at)}</span>
                </div>
              );
            })}
          </div>
        )}
        <div className="modal-actions"><button className="btn" onClick={onClose}>Chiudi</button></div>
      </div>
    </div>
  );
}

function SmsSollecitoModal({ deadlineId, onClose, onDone }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [template, setTemplate] = useState("");
  const [checked, setChecked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    api.get(`/f24/deadlines/${deadlineId}/sms-pending`).then((r) => {
      setRows(r.data.records);
      setConfigured(r.data.sms_configured);
      setTemplate(r.data.text_template);
      setChecked(new Set(r.data.records.filter((x) => x.cellulare && !x.sms_reminder_sent_at).map((x) => x.id)));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [deadlineId]);

  const toggle = (id) => setChecked((prev) => {
    const s = new Set(prev);
    if (s.has(id)) s.delete(id); else s.add(id);
    return s;
  });

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/f24/deadlines/${deadlineId}/sms-reminders`, { record_ids: [...checked] });
      setResults(r.data);
      if (r.data.failed === 0) onDone(`📱 SMS di sollecito inviati: ${r.data.sent}.`);
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const dt = (s) => (s ? new Date(s).toLocaleDateString("it-IT") : "");

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }} data-testid="f24-sms-modal">
        <h3>📱 SMS di sollecito autorizzazione</h3>
        <p className="muted" style={{ fontSize: 12.5 }}>
          Clienti con avviso email inviato ma ancora <b>in attesa</b> di risposta. Il messaggio inviato sarà:<br />
          <em>"{template.replace("{nome}", "Nome")}"</em>
        </p>
        {!configured && <div style={{ color: "#fb7185", fontSize: 13 }}>⚠️ SMShosting non configurato: inserisci le credenziali nella pagina SMS.</div>}
        {loading ? <p className="muted">Carico…</p> : rows.length === 0 ? (
          <p className="muted" data-testid="f24-sms-empty">Nessun cliente in attesa di risposta per questa scadenza. 🎉</p>
        ) : (
          <div style={{ maxHeight: 340, overflowY: "auto", margin: "10px 0" }}>
            <table style={{ width: "100%", fontSize: 13 }}>
              <thead><tr><th></th><th style={{ textAlign: "left" }}>Cliente</th><th style={{ textAlign: "left" }}>Cellulare</th><th style={{ textAlign: "left" }}>Avviso</th><th style={{ textAlign: "left" }}>Ultimo SMS</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`f24-sms-row-${r.id}`}>
                    <td><input type="checkbox" checked={checked.has(r.id)} disabled={!r.cellulare}
                      onChange={() => toggle(r.id)} data-testid={`f24-sms-check-${r.id}`} /></td>
                    <td><b>{r.client_name}</b>{r.referente ? <span className="muted"> ({r.referente})</span> : null}</td>
                    <td>{r.cellulare || <span style={{ color: "#fb7185" }}>mancante — aggiungilo da "Modifica"</span>}</td>
                    <td className="muted">{dt(r.notice_sent_at)}</td>
                    <td className="muted">{r.sms_reminder_sent_at ? `✓ ${dt(r.sms_reminder_sent_at)}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {results && (
          <div style={{ fontSize: 13, margin: "8px 0" }} data-testid="f24-sms-results">
            <b style={{ color: "#6ee7b7" }}>Inviati: {results.sent}</b>{results.failed > 0 && <b style={{ color: "#fb7185", marginLeft: 12 }}>Falliti: {results.failed}</b>}
            {results.results.filter((x) => !x.ok).map((x) => (
              <div key={x.id} style={{ color: "#fb7185" }}>✗ {x.client_name}: {x.error}</div>
            ))}
          </div>
        )}
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Chiudi</button>
          <button className="btn primary" onClick={send} disabled={busy || checked.size === 0 || !configured}
            data-testid="f24-sms-send-btn">{busy ? "Invio…" : `Invia SMS a ${checked.size} clienti`}</button>
        </div>
      </div>
    </div>
  );
}

function VociModal({ rec, onClose, onSaved }) {
  const voci = TRIBUTI.filter(([k]) => k !== "cred_comp" && Math.abs(parseAmt(rec.amounts?.[k])) > 0.001);
  const [st, setSt] = useState({ ...(rec.item_status || {}) });
  const [ravvDates, setRavvDates] = useState({ ...(rec.item_ravv_dates || {}) });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const setVoce = (k, v) => setSt((prev) => {
    const n = { ...prev };
    if (v) n[k] = v; else delete n[k];
    return n;
  });
  const save = async () => {
    setBusy(true); setErr("");
    try { await api.put(`/f24/records/${rec.id}`, { item_status: st, item_ravv_dates: ravvDates }); onSaved(); }
    catch (e) { setErr(e.response?.data?.detail || e.message); setBusy(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()} data-testid="f24-voci-modal">
        <h3>Voci F24 — {rec.client_name}</h3>
        <p className="muted" style={{ fontSize: 13, marginTop: -4 }}>
          Se il cliente autorizza solo alcuni importi e ne rinvia altri, segnalo qui voce per voce.
          Le voci <strong>💰 Versata</strong> e <strong>📅 Ravveduta</strong> non compariranno più nei solleciti
          (email e SMS) né nel promemoria mensile: il sollecito riguarderà solo gli importi rimanenti.
        </p>
        <table style={{ width: "100%", fontSize: 13.5 }}>
          <tbody>
            {voci.map(([k, label]) => (
              <tr key={k}>
                <td style={{ padding: "6px 4px" }}>{label}</td>
                <td style={{ padding: "6px 4px", textAlign: "right", fontWeight: 700 }}>{eur(parseAmt(rec.amounts?.[k]))}</td>
                <td style={{ padding: "6px 4px" }}>
                  <select value={st[k] || ""} onChange={(e) => setVoce(k, e.target.value)} style={{ padding: "4px 6px", fontSize: 12.5 }} data-testid={`f24-voce-${k}`}>
                    <option value="">— (segue lo stato generale)</option>
                    <option value="autorizzato">✓ Autorizzata dal cliente</option>
                    <option value="rinviato">⏳ Rinviata dal cliente</option>
                    <option value="versato">💰 Versata (esclusa dai solleciti)</option>
                    <option value="ravveduto">📅 Ravveduta — sistemata col ravvedimento (esclusa dai solleciti)</option>
                  </select>
                  {st[k] === "ravveduto" && (
                    <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
                      <span className="muted" style={{ fontSize: 11.5 }}>data ravv.</span>
                      <input type="date" value={ravvDates[k] || ""} style={{ padding: "3px 6px", fontSize: 12 }}
                        onChange={(e) => setRavvDates((p) => ({ ...p, [k]: e.target.value }))}
                        data-testid={`f24-ravv-date-${k}`} />
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {err && <p style={{ color: "#fb7185" }}>{err}</p>}
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Annulla</button>
          <button className="btn primary" onClick={save} disabled={busy} data-testid="f24-voci-save">{busy ? "…" : "Salva"}</button>
        </div>
      </div>
    </div>
  );
}

function ImportHelpModal({ onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 620 }} onClick={(e) => e.stopPropagation()} data-testid="f24-help-modal">
        <button className="modal-close" onClick={onClose}>✕</button>
        <h3>Come allineare le Scadenze F24 tra Preview e sito reale</h3>
        <p className="muted" style={{ fontSize: 13.5, marginTop: -4 }}>Il deploy copia solo il codice, non i dati. Per avere le stesse tabelle sul sito reale, usa Esporta/Importa. Bastano pochi passi.</p>

        <ol style={{ lineHeight: 1.8, fontSize: 14, paddingLeft: 20 }}>
          <li><b>Correggi</b> tutte le scadenze F24 qui nel Preview (comprese quelle delle tasse).</li>
          <li>Quando hai finito, clicca <b>⬇ Esporta dati F24</b> → salvi il file <code>f24_export.json</code>.</li>
          <li>Clicca <b>Deploy</b> (così il sito reale avrà gli stessi pulsanti aggiornati).</li>
          <li>Vai sul <b>sito reale</b> (crm.casalisilvia.com) → <b>Scadenze F24</b>.</li>
          <li>Clicca <b>⬆ Importa dati F24 (JSON)</b> e carica il file <code>f24_export.json</code>.</li>
          <li>Conferma. Il sito reale avrà <b>esattamente le stesse tabelle</b> del Preview. ✅</li>
        </ol>

        <div className="panel" style={{ borderColor: "#d97706", fontSize: 13 }}>
          ⚠️ <b>Importante</b>: l'importazione <b>sostituisce completamente</b> tutte le scadenze e i dati F24 dell'ambiente in cui la lanci. Importa il file <b>solo sul sito reale</b> (quello che vuoi aggiornare), dopo aver esportato dal Preview corretto.
        </div>

        <div className="modal-actions">
          <button className="btn primary" onClick={onClose} data-testid="f24-help-close">Ho capito</button>
        </div>
      </div>
    </div>
  );
}

function ImportScadenzeModal({ importing, onClose, onImport }) {
  const [pdf, setPdf] = useState(null);
  const [xls, setXls] = useState(null);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()} data-testid="f24-import-modal">
        <button className="modal-close" onClick={onClose}>✕</button>
        <h3>Importa scadenze F24</h3>
        <p className="muted" style={{ fontSize: 13.5, marginTop: -6 }}>Carica il <b>PDF "Scadenze"</b> (importi e suddivisione) e l'<b>Excel esiti</b> (per segnare versato/non versato e verificare i totali).</p>
        <div className="field">
          <label>1) PDF Scadenze (importi)</label>
          <input type="file" accept=".pdf" onChange={(e) => setPdf(e.target.files[0])} data-testid="f24-import-pdf-input" />
          {pdf && <div className="muted" style={{ fontSize: 12 }}>✓ {pdf.name}</div>}
        </div>
        <div className="field">
          <label>2) Excel esiti F24 (stato pagamenti)</label>
          <input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setXls(e.target.files[0])} data-testid="f24-import-excel-input" />
          {xls && <div className="muted" style={{ fontSize: 12 }}>✓ {xls.name}</div>}
        </div>
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Annulla</button>
          <button className="btn primary" disabled={!pdf || !xls || importing} onClick={() => onImport(pdf, xls)} data-testid="f24-import-confirm-btn">
            {importing ? "Importo…" : "Importa"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ImportReportModal({ report, onClose }) {
  const r = report || {};
  const dIT = (s) => { const d = new Date(s); return isNaN(d) ? s : d.toLocaleDateString("it-IT"); };
  const eur2 = (v) => "€ " + Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 860, width: "94vw" }} onClick={(e) => e.stopPropagation()} data-testid="f24-import-report">
        <button className="modal-close" onClick={onClose}>✕</button>
        <h3>Import scadenze F24 — riepilogo</h3>

        <div className="panel" style={{ display: "flex", gap: 22, flexWrap: "wrap", marginBottom: 16 }}>
          <div>F24 elaborati: <b>{r.f24_total}</b></div>
          <div style={{ color: "#6ee7b7" }}>Pagati: <b>{r.paid}</b></div>
          <div style={{ color: "#fca5a5" }}>Non pagati: <b>{r.unpaid}</b></div>
          <div className="muted">Nuovi: <b>{r.created}</b> · Aggiornati: <b>{r.updated}</b></div>
        </div>
        {r.deadlines_created?.length > 0 && (
          <p className="muted" style={{ fontSize: 13 }}>Nuove scadenze create: {r.deadlines_created.map((d) => d.label).join(", ")}</p>
        )}

        <h4 style={{ margin: "6px 0 8px", color: "#fca5a5" }}>🔴 F24 NON pagati ({(r.unpaid_list || []).length})</h4>
        <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 12 }}>
          <table style={{ margin: 0 }}>
            <thead><tr><th>Scadenza</th><th>Cliente</th><th>Dettaglio</th><th style={{ textAlign: "right" }}>Importo</th></tr></thead>
            <tbody>
              {(r.unpaid_list || []).map((u, i) => (
                <tr key={i} data-testid={`f24-report-unpaid-${i}`}>
                  <td style={{ whiteSpace: "nowrap" }}>{dIT(u.date)}</td>
                  <td>{u.client}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{u.detail ? u.detail.map((x) => `Mod.${x.mod} ${eur2(x.net)} (${x.status})`).join(" · ") : "—"}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>{eur2(u.total)}</td>
                </tr>
              ))}
              {(r.unpaid_list || []).length === 0 && <tr><td colSpan={4} className="muted" style={{ textAlign: "center" }}>Nessuno 🎉</td></tr>}
            </tbody>
          </table>
        </div>

        {r.mixed_modello?.length > 0 && (
          <>
            <h4 style={{ margin: "16px 0 8px", color: "#93c5fd" }}>➗ Clienti con modelli misti — pagato + non pagato ({r.mixed_modello.length})</h4>
            <div style={{ maxHeight: 150, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 12 }}>
              <table style={{ margin: 0 }}>
                <thead><tr><th>Scadenza</th><th>Cliente</th><th>Dettaglio modelli</th></tr></thead>
                <tbody>
                  {r.mixed_modello.map((m, i) => (
                    <tr key={i} data-testid={`f24-report-mixed-${i}`}>
                      <td style={{ whiteSpace: "nowrap" }}>{dIT(m.date)}</td>
                      <td>{m.client}</td>
                      <td className="muted" style={{ fontSize: 12 }}>{m.dettaglio.map((x) => `Mod.${x.mod} ${eur2(x.net)} ${x.pagato ? "✅ pagato" : "🔴 " + x.stato}`).join(" · ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {r.mismatches?.length > 0 && (
          <>
            <h4 style={{ margin: "16px 0 8px", color: "#fbbf24" }}>⚠️ Totali che NON quadrano (PDF vs Excel) ({r.mismatches.length}) — da verificare</h4>
            <div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 12 }}>
              <table style={{ margin: 0 }}>
                <thead><tr><th>Scadenza</th><th>Cliente</th><th style={{ textAlign: "right" }}>PDF</th><th style={{ textAlign: "right" }}>Excel</th></tr></thead>
                <tbody>
                  {r.mismatches.map((x, i) => (
                    <tr key={i} data-testid={`f24-report-mismatch-${i}`}>
                      <td style={{ whiteSpace: "nowrap" }}>{dIT(x.date)}</td>
                      <td>{x.client}</td>
                      <td style={{ textAlign: "right", fontWeight: 700 }}>{eur2(x.pdf_total)}</td>
                      <td style={{ textAlign: "right", color: "#9aa4bf" }}>{eur2(x.xls_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="muted" style={{ fontSize: 12.5 }}>Ho usato l'importo del PDF. Spesso la differenza è perché nell'Excel c'è anche un modello "compilato/non versato" non incluso nel PDF. Controlla e dimmi.</p>
          </>
        )}

        {r.status_unknown?.length > 0 && (
          <p className="muted" style={{ fontSize: 12.5, marginTop: 12 }}>
            Clienti senza corrispondenza nell'Excel (stato non impostato, verifica manuale): {r.status_unknown.map((x) => `${x.client} (${dIT(x.date)})`).join(", ")}.
          </p>
        )}

        <div className="modal-actions">
          <button className="btn primary" onClick={onClose} data-testid="f24-import-report-close">Ho capito</button>
        </div>
      </div>
    </div>
  );
}

function RedditiImportModal({ rows, deadlineLabel, onClose, onConfirm }) {
  const [data, setData] = useState(rows.map((r) => ({ ...r, amounts: { ...r.amounts } })));
  const [busy, setBusy] = useState(false);
  const valid = data.filter((r) => !r.error);
  const setField = (i, k, v) => setData(data.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const setAmt = (i, k, v) => setData(data.map((r, j) => (j === i ? { ...r, amounts: { ...r.amounts, [k]: parseFloat(String(v).replace(",", ".")) || 0 } } : r)));
  const rowTotal = (a) => (a.unico || 0) + (a.inps || 0) + (a.cciaa || 0) - (a.cred_comp || 0);
  const confirm = async () => {
    setBusy(true);
    try { await onConfirm(valid); }
    catch (e) { alert(e.response?.data?.detail || e.message); setBusy(false); }
  };
  const FIELDS = [["unico", "Modello Redditi (imposte)"], ["inps", "INPS"], ["cciaa", "Diritto CCIAA"], ["cred_comp", "Crediti comp."]];
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 820 }} data-testid="f24-redditi-import-modal">
        <h3>Importi letti dai PDF Redditi</h3>
        <p className="muted" style={{ fontSize: 13 }}>Controlla gli importi (sono <b>suggerimenti</b> letti dalle dichiarazioni). Se il cliente è <b>rateizzato</b>, l'importo di <b>ogni rata</b> va nella colonna <b>Modello Redditi</b> della rispettiva scadenza (la 1ª sulla scadenza aperta, le altre nelle mensili — create in automatico se mancano). Il dettaglio Imposte/INPS qui sotto è usato solo se NON ci sono rate.</p>
        {data.map((r, i) => (
          <div key={i} className="panel" style={{ marginBottom: 10, padding: 12 }} data-testid={`f24-redditi-parsed-${i}`}>
            {r.error ? (
              <div style={{ color: "#fb7185", fontSize: 13 }}>⚠️ {r.filename}: {r.error}</div>
            ) : (
              <>
                <div className="grid-2" style={{ marginBottom: 6 }}>
                  <div className="field" style={{ margin: 0 }}><label style={{ fontSize: 12 }}>Cliente</label>
                    <input value={r.client_name} onChange={(e) => setField(i, "client_name", e.target.value)} data-testid={`f24-redditi-name-${i}`} /></div>
                  <div className="field" style={{ margin: 0 }}><label style={{ fontSize: 12 }}>Email</label>
                    <input value={r.client_email || ""} onChange={(e) => setField(i, "client_email", e.target.value)} data-testid={`f24-redditi-email-${i}`} /></div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
                  {FIELDS.map(([k, l]) => (
                    <div className="field" key={k} style={{ margin: 0 }}><label style={{ fontSize: 11 }}>{l} (€)</label>
                      <input value={r.amounts[k] || ""} onChange={(e) => setAmt(i, k, e.target.value)} placeholder="0,00" style={{ fontSize: 12 }} data-testid={`f24-redditi-${k}-${i}`} /></div>
                  ))}
                </div>
                <div style={{ textAlign: "right", marginTop: 6, fontWeight: 700 }}>Totale: {eur(rowTotal(r.amounts))}{!r.company_id && <span style={{ color: "#fbbf24", fontWeight: 400, fontSize: 12 }}> · nessuna anagrafica abbinata</span>}</div>
                {(r.rate || []).length > 0 && (
                  <div style={{ marginTop: 8, borderTop: "1px solid var(--border)", paddingTop: 8 }} data-testid={`f24-redditi-rate-${i}`}>
                    <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 4 }}>Rate lette dal PDF · ogni rata va nella colonna "Modello Redditi" della sua scadenza:</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {r.rate.map((rt, k) => (
                        <span key={k} className="pill" style={{ fontSize: 11.5, background: "rgba(56,189,248,.12)", border: "1px solid var(--border)" }}>
                          {dShort(rt.due_date)} · {eur(rt.amount)} → Modello Redditi
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {r.second_acconto && r.second_acconto.amount > 0 && (
                  <div style={{ marginTop: 6 }} data-testid={`f24-redditi-iiacc-${i}`}>
                    <span className="pill" style={{ fontSize: 11.5, background: "rgba(245,158,11,.15)", border: "1px solid var(--border)" }}>
                      II acconto · {dShort(r.second_acconto.due_date)} · {eur(r.second_acconto.amount)} → Modello Redditi
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Annulla</button>
          <button className="btn primary" onClick={confirm} disabled={busy || !valid.length} data-testid="f24-redditi-confirm">{busy ? "Creo…" : (valid.length === 1 ? "Crea 1 riga" : `Crea ${valid.length} righe`)}</button>
        </div>
      </div>
    </div>
  );
}

function NewDeadlineModal({ onClose, onSave }) {
  const PRESETS = [
    ["2026-07-20", "Redditi 2025 · saldo + 1° acconto"],
    ["2026-07-30", "Redditi 2025 · saldo + 1° acconto (+0,40%)"],
    ["2026-08-20", "Redditi 2025 · rata"],
  ];
  const [due, setDue] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!due || !label.trim()) { alert("Inserisci data ed etichetta."); return; }
    setBusy(true);
    try { await onSave({ due_date: due, label: label.trim() }); }
    catch (e) { alert(e.response?.data?.detail || e.message); setBusy(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="f24-new-deadline-modal">
        <h3>Nuova scadenza</h3>
        <p className="muted" style={{ fontSize: 13 }}>Per le tasse 2025 (Redditi): scegli un preset oppure imposta data ed etichetta.</p>
        <div className="field"><label>Preset rapidi</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {PRESETS.map(([d, l]) => (
              <button key={d} className="btn sm" type="button" onClick={() => { setDue(d); setLabel(l); }} data-testid={`f24-preset-${d}`}>{dIT(d)}</button>
            ))}
          </div>
        </div>
        <div className="field"><label>Data scadenza</label>
          <input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="f24-new-deadline-date" /></div>
        <div className="field"><label>Etichetta</label>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="es. Redditi 2025 · saldo" data-testid="f24-new-deadline-label" /></div>
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Annulla</button>
          <button className="btn primary" onClick={save} disabled={busy} data-testid="f24-new-deadline-save">{busy ? "Creo…" : "Crea scadenza"}</button>
        </div>
      </div>
    </div>
  );
}



function RecapitiDaPicker({ companies, persons, f, setF }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState("");
  const options = [
    ...companies.map((c) => ({ type: "company", label: c.name || "", email: c.email || "", phone: c.phone || "", ref: c.referente || "" })),
    ...persons.map((p) => ({ type: "person", label: `${p.first_name || ""} ${p.last_name || ""}`.trim(), email: p.email || "", phone: p.phone || "", ref: p.first_name || "" })),
  ];
  const ql = q.trim().toLowerCase();
  const matches = ql.length >= 2
    ? options.filter((o) => o.label.toLowerCase().includes(ql) || o.email.toLowerCase().includes(ql)).slice(0, 8)
    : [];
  const pick = (o) => {
    setF((prev) => ({
      ...prev,
      client_email: o.email || prev.client_email,
      cellulare: o.phone || prev.cellulare,
      referente: o.ref || prev.referente,
    }));
    setDone(o.label);
    setQ(""); setOpen(false);
  };
  return (
    <div className="field" style={{ position: "relative" }}>
      <label>📇 Recapiti presi da un altro contatto (facoltativo)</label>
      <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder='Es. "Susi Sambi" o "Casa del Ciclo": copio qui email, cellulare e referente'
        data-testid="f24-recapiti-da" />
      {open && matches.length > 0 && (
        <div className="ac-list" style={{ position: "absolute", zIndex: 30, left: 0, right: 0 }}>
          {matches.map((o, i) => (
            <div key={i} className="ac-item" onMouseDown={() => pick(o)} data-testid={`f24-recapiti-opt-${i}`}>
              <span className="ac-ico">{o.type === "company" ? "🏢" : "👤"}</span>
              <span className="ac-label">{o.label}</span>
              <span className="ac-email">{o.email}{o.phone ? ` · ${o.phone}` : ""}</span>
            </div>
          ))}
        </div>
      )}
      <p className="muted" style={{ fontSize: 11.5, margin: "4px 0 0" }}>
        {done
          ? <>✅ Recapiti copiati da <strong>{done}</strong>: il nome del cliente resta quello scritto sopra.</>
          : "Utile per i soci: ogni riga tiene il SUO nome, ma email/cellulare/referente arrivano dal contatto di riferimento (es. l'amministratore). I dati vengono copiati: puoi ritoccarli."}
      </p>
    </div>
  );
}

function AnagraficaPicker({ companies, persons, f, setF }) {
  const linked = f.company_id
    ? companies.find((c) => c.id === f.company_id)?.name
    : f.person_id
      ? (() => { const p = persons.find((x) => x.id === f.person_id); return p ? `${p.first_name || ""} ${p.last_name || ""}`.trim() : null; })()
      : null;
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const options = [
    ...companies.map((c) => ({ type: "company", id: c.id, label: c.name || "", email: c.email || "" })),
    ...persons.map((p) => ({ type: "person", id: p.id, label: `${p.first_name || ""} ${p.last_name || ""}`.trim(), email: p.email || "" })),
  ];
  const ql = q.trim().toLowerCase();
  const matches = ql.length >= 2
    ? options.filter((o) => o.label.toLowerCase().includes(ql) || o.email.toLowerCase().includes(ql)).slice(0, 8)
    : [];

  const pick = (o) => {
    setF({
      ...f,
      company_id: o.type === "company" ? o.id : "",
      person_id: o.type === "person" ? o.id : "",
      client_name: o.label || f.client_name,
      client_email: o.email || f.client_email,
    });
    setQ(""); setOpen(false);
  };
  const unlink = () => setF({ ...f, company_id: "", person_id: "" });

  return (
    <div className="field" style={{ position: "relative" }}>
      <label>Anagrafica collegata (azienda o contatto)</label>
      {linked ? (
        <div className="ac-linked" data-testid="f24-edit-linked">
          <span>{f.company_id ? "🏢" : "👤"} {linked}</span>
          <button type="button" className="ac-unlink" onClick={unlink} title="Scollega" data-testid="f24-edit-unlink">×</button>
        </div>
      ) : (
        <>
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            placeholder="Inizia a scrivere: cerco tra aziende e contatti…"
            data-testid="f24-edit-anagrafica-search"
          />
          {open && matches.length > 0 && (
            <div className="ac-list" data-testid="f24-edit-anagrafica-list">
              {matches.map((o) => (
                <div key={o.type + o.id} className="ac-item" onMouseDown={() => pick(o)} data-testid={`f24-edit-anagrafica-opt-${o.type}-${o.id}`}>
                  <span className="ac-ico">{o.type === "company" ? "🏢" : "👤"}</span>
                  <span className="ac-label">{o.label}</span>
                  {o.email && <span className="ac-email">{o.email}</span>}
                </div>
              ))}
            </div>
          )}
          {open && ql.length >= 2 && matches.length === 0 && (
            <div className="ac-list"><div className="ac-item ac-empty">Nessun risultato per «{q}»</div></div>
          )}
        </>
      )}
      {rec_new_hint(linked)}
    </div>
  );
}

function rec_new_hint(linked) {
  if (linked) return null;
  return <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Scegliendo un'anagrafica, nome ed email si compilano da soli e le modifiche email si sincronizzano con Contatti/Aziende.</div>;
}

function EditModal({ rec, companies, persons, onClose, onSaved }) {
  const [f, setF] = useState({
    client_name: rec.client_name || "", client_email: rec.client_email || "",
    referente: rec.referente || "", referente_genere: rec.referente_genere || "",
    cellulare: rec.cellulare || "",
    company_id: rec.company_id || "", person_id: rec.person_id || "", notes: rec.notes || "",
    amounts: { ...(rec.amounts || {}) },
    ravved: (rec.ravvedimento_dates || []).join(", "),
  });
  const [busy, setBusy] = useState(false);
  const [regulars, setRegulars] = useState([]);
  const [fisso, setFisso] = useState(false);
  const setAmt = (k, v) => setF({ ...f, amounts: { ...f.amounts, [k]: v } });

  const regMatch = (list) => list.find((ru) =>
    (ru.person_id && ru.person_id === (rec.person_id || f.person_id)) ||
    (ru.company_id && ru.company_id === (rec.company_id || f.company_id)) ||
    ((ru.client_name || "").trim().toLowerCase() === (rec.client_name || f.client_name || "").trim().toLowerCase() && (ru.client_name || "").trim()));
  useEffect(() => {
    api.get("/f24/regulars").then((r) => {
      setRegulars(r.data);
      setFisso(Boolean(regMatch(r.data)));
    }).catch(() => {});
  }, []); // eslint-disable-line

  const saveFisso = async () => {
    const cur = regMatch(regulars);
    if (fisso) {
      await api.post("/f24/regulars", {
        client_name: f.client_name, client_email: f.client_email || null,
        company_id: f.company_id || null, person_id: f.person_id || null,
        referente: f.referente || null, referente_genere: f.referente_genere || null,
        cellulare: f.cellulare || null,
      });
    } else if (cur) {
      await api.delete(`/f24/regulars/${cur.id}`);
    }
  };

  const save = async () => {
    setBusy(true);
    const amounts = {};
    TRIBUTI.forEach(([k]) => { amounts[k] = parseFloat(String(f.amounts[k] || "0").replace(",", ".")) || 0; });
    const rav = f.ravved.split(",").map((s) => s.trim()).filter(Boolean);
    try {
      if (rec._new) {
        await api.post(`/f24/deadlines/${rec.deadline_id}/records`, {
          client_name: f.client_name, client_email: f.client_email,
          referente: f.referente, referente_genere: f.referente_genere, cellulare: f.cellulare,
          company_id: f.company_id || null, person_id: f.person_id || null, amounts, notes: f.notes,
        });
      } else {
        const res = await api.put(`/f24/records/${rec.id}`, {
          client_name: f.client_name, client_email: f.client_email,
          referente: f.referente, referente_genere: f.referente_genere, cellulare: f.cellulare,
          company_id: f.company_id || null, person_id: f.person_id || null,
          amounts, notes: f.notes, ravvedimento_dates: rav,
        });
        const p = res.data?.propagated || 0;
        const an = res.data?.anagrafica_updated;
        if (p > 0 || an) {
          alert(`Modifica propagata: ${p} altre scadenze aggiornate${an ? ` + ${an} in anagrafica` : ""}.`);
        }
      }
      await saveFisso();
      onSaved();
    } catch (e) { alert(e.response?.data?.detail || e.message); setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="f24-edit-modal">
        <h3>{rec._new ? "Aggiungi cliente" : "Modifica riga F24"}</h3>
        <div className="field"><label>Cliente</label><input value={f.client_name} onChange={(e) => setF({ ...f, client_name: e.target.value })} data-testid="f24-edit-name" /></div>
        <div className="field"><label>Email cliente</label><input value={f.client_email} onChange={(e) => setF({ ...f, client_email: e.target.value })} data-testid="f24-edit-email" /></div>
        <div className="field"><label>Cellulare (per SMS di sollecito)</label>
          <input value={f.cellulare} onChange={(e) => setF({ ...f, cellulare: e.target.value })} placeholder="es. 328 4510569" data-testid="f24-edit-cellulare" /></div>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
          <div className="field"><label>Referente (la persona a cui scrivi, es. "Elena")</label>
            <input value={f.referente} onChange={(e) => setF({ ...f, referente: e.target.value })} placeholder="nome di battesimo" data-testid="f24-edit-referente" /></div>
          <div className="field"><label>Genere</label>
            <select value={f.referente_genere} onChange={(e) => setF({ ...f, referente_genere: e.target.value })} data-testid="f24-edit-genere" style={{ width: "100%" }}>
              <option value="">—</option>
              <option value="f">Donna</option>
              <option value="m">Uomo</option>
            </select></div>
        </div>
        <p className="muted" style={{ fontSize: 11.5, marginTop: -6 }}>Con referente e genere, le email AI iniziano con "Ciao {f.referente || "nome"}," e usano il tu con gli accordi giusti. Valgono per tutte le scadenze di questo cliente.</p>
        <AnagraficaPicker companies={companies} persons={persons} f={f} setF={setF} />
        <RecapitiDaPicker companies={companies} persons={persons} f={f} setF={setF} />
        <label style={{ display: "flex", alignItems: "center", gap: 8, margin: "2px 0 10px", cursor: "pointer", fontSize: 13.5 }}>
          <input type="checkbox" checked={fisso} onChange={(e) => setFisso(e.target.checked)} style={{ width: 16, margin: 0 }} data-testid="f24-edit-fisso" />
          📌 <strong>Cliente fisso</strong>: riproponilo automaticamente in ogni nuova scadenza (con importi da compilare)
        </label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {TRIBUTI.map(([k, l]) => (
            <div className="field" key={k}><label>{l} (€)</label>
              <input value={f.amounts[k] || ""} onChange={(e) => setAmt(k, e.target.value)} placeholder="0,00" data-testid={`f24-edit-${k}`} /></div>
          ))}
        </div>
        {!rec._new && <div className="field"><label>Date ravvedimenti (gg/mm/aaaa, separate da virgola → salvate come aaaa-mm-gg)</label>
          <input value={f.ravved} onChange={(e) => setF({ ...f, ravved: e.target.value })} placeholder="2026-07-10, 2026-08-20" /></div>}
        <div className="field"><label>Note</label><textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Annulla</button>
          <button className="btn primary" onClick={save} disabled={busy} data-testid="f24-edit-save">{busy ? "Salvo…" : "Salva"}</button>
        </div>
      </div>
    </div>
  );
}

function DraftModal({ data, onClose, onSent, onSaved }) {
  const { record, kind } = data;
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [to, setTo] = useState(record.client_email || "");
  const [ccStr, setCcStr] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ivaSplit, setIvaSplit] = useState(null); // dettaglio 2 saloni (solo Gallo)
  const [savedDraft, setSavedDraft] = useState(false);
  const [fullPreview, setFullPreview] = useState(null);
  const [ref, setRef] = useState(record.referente || "");
  const [refBusy, setRefBusy] = useState(false);
  const tributiPresenti = TRIBUTI.filter(([k]) => Math.abs(parseAmt(record.amounts?.[k])) > 0.001);
  const vociEscluse = (k) => ["versato", "ravveduto"].includes((record.item_status || {})[k]);
  const [included, setIncluded] = useState(() => new Set(tributiPresenti.filter(([k]) => !vociEscluse(k)).map(([k]) => k)));
  const toggleInc = (k) => setIncluded((prev) => {
    const s = new Set(prev);
    if (s.has(k)) s.delete(k); else s.add(k);
    return s;
  });
  const isGallo = (record.client_name || "").toLowerCase().includes("gallo annamaria");
  const isFede = (record.client_name || "").toLowerCase().includes("fede hotel");
  const [fedeBusy, setFedeBusy] = useState(false);

  const onFedeZip = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setErr(""); setFedeBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/f24/records/${record.id}/fede-iva-mail`, fd, { timeout: 180000 });
      setBody(r.data.body_html);
      if (r.data.subject) setSubject(r.data.subject);
    } catch (ex) { setErr(ex.response?.data?.detail || ex.message); }
    setFedeBusy(false);
  };

  const load = (force, include) => {
    setLoading(true); setErr("");
    const ep = kind === "notice" ? "draft-notice" : "draft-reminder";
    api.post(`/f24/records/${record.id}/${ep}${force ? "?force=true" : ""}`, include ? { include } : {}).then((r) => {
      setSubject(r.data.subject); setBody(r.data.body_html);
      if (r.data.client_email) setTo(r.data.client_email);
      if (r.data.cc && r.data.cc.length) setCcStr(r.data.cc.join(", "));
      if (r.data.referente !== undefined) setRef(r.data.referente || "");
      setSavedDraft(!!r.data.saved);
      setLoading(false);
    }).catch((e) => { setErr(e.response?.data?.detail || e.message); setLoading(false); });
  };
  useEffect(() => { load(false); }, [record.id, kind]); // eslint-disable-line

  // anteprima completa (con pulsanti e nota ravvedimento) sempre aggiornata
  useEffect(() => {
    if (loading || !body) return;
    const t = setTimeout(() => {
      api.post(`/f24/records/${record.id}/email-preview`, { kind, body_html: body })
        .then((r) => setFullPreview(r.data.html)).catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [body, loading]); // eslint-disable-line

  const applyRef = async () => {
    setRefBusy(true); setErr("");
    try {
      await api.put(`/f24/records/${record.id}`, { referente: ref.trim() });
      load(true);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    setRefBusy(false);
  };

  const saveDraft = async () => {
    setBusy(true); setErr("");
    try {
      await api.put(`/f24/records/${record.id}/draft`, { kind, subject, body_html: body });
      onSaved();
    } catch (e) { setErr(e.response?.data?.detail || e.message); setBusy(false); }
  };

  const onIvaPdf = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setErr("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/f24/parse-iva-split`, fd);
      setBody((b) => b + "\n" + r.data.block_html);
      setIvaSplit(r.data);
    } catch (ex) { setErr(ex.response?.data?.detail || ex.message); }
  };

  const send = async () => {
    setBusy(true); setErr("");
    const cc = ccStr.split(/[;,]/).map((x) => x.trim()).filter((x) => /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/.test(x));
    try {
      await api.post(`/f24/records/${record.id}/send`, { subject, body_html: body, to: to.trim(), kind, cc });
      onSent();
    } catch (e) { setErr(e.response?.data?.detail || e.message); setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 760 }} onClick={(e) => e.stopPropagation()} data-testid="f24-draft-modal">
        <h3>{kind === "notice" ? "Avviso F24 (con pulsanti Autorizza / Rinvia)" : "Sollecito F24 non pagato"}</h3>
        {loading ? <p className="muted">Claude sta scrivendo l'email…</p> : (
          <>
            {savedDraft && (
              <p style={{ fontSize: 12.5, color: "#fbbf24", margin: "0 0 10px" }} data-testid="f24-draft-saved-badge">
                📝 Caricata la tua <strong>bozza salvata</strong> (non ancora inviata).{" "}
                <button className="btn sm" onClick={() => load(true)} data-testid="f24-draft-regen">↻ Rigenera con AI</button>
              </p>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div className="field"><label>Destinatario (una sola email)</label><input value={to} onChange={(e) => setTo(e.target.value)} data-testid="f24-draft-to" /></div>
              <div className="field"><label>Copia a (CC) — più indirizzi separati da virgola</label>
                <input value={ccStr} onChange={(e) => setCcStr(e.target.value)} placeholder="es. socio@azienda.it, amministrazione@azienda.it" data-testid="f24-draft-cc" /></div>
            </div>
            <div className="field">
              <label>Nome per il saluto (referente) {!ref.trim() && <span style={{ color: "#fbbf24" }}>— manca: la mail inizierà con "Buongiorno"</span>}</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="es. Marinella" data-testid="f24-draft-referente" style={{ flex: 1 }} />
                <button className="btn sm" onClick={applyRef} disabled={refBusy || loading} data-testid="f24-draft-referente-apply" title="Salva il nome sul cliente e rigenera la mail col saluto giusto">
                  {refBusy ? "…" : "↻ Rigenera col nome"}
                </button>
              </div>
            </div>
            <div className="field"><label>Oggetto</label><input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="f24-draft-subject" /></div>
            {tributiPresenti.length > 1 && (
              <div style={{ margin: "0 0 12px", padding: "8px 10px", border: "1px solid rgba(255,255,255,.12)", borderRadius: 10 }} data-testid="f24-draft-include">
                <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>Voci da comunicare in questa email (togli la spunta a quelle da NON inviare):</div>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                  {tributiPresenti.map(([k, label]) => (
                    <label key={k} style={{ display: "flex", gap: 6, alignItems: "center", margin: 0, fontSize: 13 }}>
                      <input type="checkbox" checked={included.has(k)} onChange={() => toggleInc(k)} style={{ width: 16 }} data-testid={`f24-draft-inc-${k}`} />
                      {label} ({fmtNum(parseAmt(record.amounts?.[k])) || "0"})
                      {(record.item_status || {})[k] === "versato" && <span style={{ color: "#4ade80", fontSize: 11.5 }}>💰 versata</span>}
                      {(record.item_status || {})[k] === "ravveduto" && <span style={{ color: "#fbbf24", fontSize: 11.5 }}>📅 ravveduta</span>}
                    </label>
                  ))}
                  <button className="btn sm" onClick={() => load(true, [...included])} disabled={included.size === 0 || busy} data-testid="f24-draft-regen-selected">
                    ↻ Rigenera la mail con le voci selezionate
                  </button>
                </div>
              </div>
            )}
            <div className="field"><label>Testo (modificabile — generato da Claude, da approvare)</label>
              <textarea rows={9} value={body} onChange={(e) => setBody(e.target.value)} data-testid="f24-draft-body" style={{ fontFamily: "monospace", fontSize: 12.5 }} /></div>
            <div style={{ margin: "0 0 4px" }}>
              <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
                👇 <strong>Anteprima della email completa</strong> come arriverà al cliente{kind === "notice" ? " (con i pulsanti Autorizza/Rinvia e la nota ravvedimento € 10,00, aggiunti in automatico all'invio)" : ""}:
              </div>
              {fullPreview
                ? <div data-testid="f24-full-preview"><iframe title="anteprima-email" sandbox="" srcDoc={fullPreview} style={{ width: "100%", height: 460, border: "1px solid rgba(255,255,255,.15)", borderRadius: 10, background: "#f4f5f7" }} /></div>
                : <p className="muted" style={{ fontSize: 12.5 }}>Genero l'anteprima…</p>}
            </div>
            {isFede && kind === "notice" && (
              <div style={{ margin: "0 0 10px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <label className="btn sm" style={{ cursor: "pointer", opacity: fedeBusy ? 0.6 : 1 }} data-testid="f24-fede-iva-btn">
                  📎 Carica registri IVA (ZIP) → mail con controlli
                  <input type="file" accept=".zip,.pdf" onChange={onFedeZip} style={{ display: "none" }} disabled={fedeBusy} />
                </label>
                <span className="muted" style={{ fontSize: 12 }} data-testid="f24-fede-iva-hint">
                  {fedeBusy ? "⏳ Claude sta analizzando i registri e scrivendo la mail…" : "Sostituisce il testo con la mail di liquidazione IVA: numeri del mese + controlli sui registri."}
                </span>
              </div>
            )}
            {isGallo && kind === "notice" && (
              <div style={{ margin: "0 0 10px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <label className="btn sm" style={{ cursor: "pointer" }} data-testid="f24-iva-split-btn">
                  📎 Carica dettaglio IVA (2 saloni)
                  <input type="file" accept=".pdf" onChange={onIvaPdf} style={{ display: "none" }} />
                </label>
                {ivaSplit ? (
                  <span style={{ fontSize: 12, color: "#6ee7b7" }} data-testid="f24-iva-split-ok">
                    ✓ Inserito{ivaSplit.periodo ? ` (${ivaSplit.periodo})` : ""}: Filangieri € {Number(ivaSplit.salone1).toLocaleString("it-IT", { minimumFractionDigits: 2 })} · Piscicelli € {Number(ivaSplit.salone2).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                  </span>
                ) : (
                  <span className="muted" style={{ fontSize: 12 }}>PDF liquidazione IVA mensile: aggiunge nella mail l'IVA divisa tra i due saloni.</span>
                )}
              </div>
            )}
            <p className="muted" style={{ fontSize: 12 }}>Anteprima HTML del corpo:</p>
            <div style={{ background: "#fff", color: "#1f2937", padding: 14, borderRadius: 10, maxHeight: 180, overflow: "auto", fontSize: 13 }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(body) }} />
          </>
        )}
        {err && <p style={{ color: "#fb7185" }} data-testid="f24-draft-err">{err}</p>}
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Annulla</button>
          <button className="btn" onClick={saveDraft} disabled={busy || loading} data-testid="f24-draft-save">💾 Salva bozza</button>
          <button className="btn primary" onClick={send} disabled={busy || loading} data-testid="f24-draft-send">{busy ? "Invio…" : "Approva e invia"}</button>
        </div>
      </div>
    </div>
  );
}

function ReminderModal({ month, onClose, onDone }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { api.get("/f24/reminder-config").then((r) => setCfg(r.data)); }, []);

  const save = async () => {
    setBusy(true); setErr("");
    try { const r = await api.put("/f24/reminder-config", cfg); setCfg(r.data); onDone("Promemoria salvati."); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const improve = async () => {
    setBusy(true); setErr("");
    try { const r = await api.post("/f24/reminder-config/improve", { text: cfg.template }); setCfg({ ...cfg, template: r.data.text }); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const runNow = async () => {
    if (!window.confirm(`Preparare i solleciti per i non pagati di ${month}? Finiranno tutti nella coda "Da approvare": li invii tu dopo averli controllati.`)) return;
    setBusy(true); setErr("");
    try { const r = await api.post("/f24/reminder/run", { month }); onDone(`Solleciti preparati: ${r.data.sent} messi in "Da approvare", ${r.data.skipped} saltati, ${r.data.no_email} senza email.`); onClose(); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
    setBusy(false);
  };

  if (!cfg) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 760 }} onClick={(e) => e.stopPropagation()} data-testid="f24-reminder-modal">
        <h3>⏰ Promemoria automatici F24 non pagati</h3>
        <div style={{ display: "flex", gap: 20, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center", margin: 0 }}>
            <input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} data-testid="f24-reminder-enabled" style={{ width: 18 }} />
            Invio automatico attivo
          </label>
          <label style={{ display: "flex", gap: 8, alignItems: "center", margin: 0 }}>
            Ogni mese il giorno
            <input type="number" min={1} max={28} value={cfg.day} onChange={(e) => setCfg({ ...cfg, day: parseInt(e.target.value, 10) || 20 })} style={{ width: 70 }} data-testid="f24-reminder-day" />
          </label>
        </div>
        <div className="field"><label>Oggetto</label>
          <input value={cfg.subject} onChange={(e) => setCfg({ ...cfg, subject: e.target.value })} data-testid="f24-reminder-subject" /></div>
        <div className="field"><label>Bozza del sollecito (HTML). Segnaposto: {"{nome}"} {"{mese}"} {"{anno}"} {"{righe}"} {"{totale}"}</label>
          <textarea rows={9} value={cfg.template} onChange={(e) => setCfg({ ...cfg, template: e.target.value })} data-testid="f24-reminder-template" style={{ fontFamily: "monospace", fontSize: 12.5 }} /></div>
        <button className="btn sm" onClick={improve} disabled={busy} data-testid="f24-reminder-improve">✨ Migliora con AI</button>
        {err && <p style={{ color: "#fb7185" }} data-testid="f24-reminder-err">{err}</p>}
        <p className="muted" style={{ fontSize: 12, marginTop: 14 }}>Quando attivo, il giorno {cfg.day} di ogni mese il sistema invia a ogni cliente con F24 ancora <strong>aperti</strong> (non pagati o respinti dalla banca) un <strong>unico riepilogo</strong> di tutte le sue posizioni — una sola email per cliente al mese.</p>
        <div className="modal-actions" style={{ justifyContent: "space-between" }}>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn sm" onClick={runNow} disabled={busy || !month} data-testid="f24-reminder-runnow">Invia solleciti ora ({month})</button>
            <button className="btn sm primary" data-testid="f24-reminder-rigenera" disabled={busy}
                    title="Fa tutto nell'ordine giusto: rifiuta i solleciti in coda, azzera il blocco mensile e rigenera i solleciti corretti (scadenze fino a ieri)"
                    onClick={async () => {
                      if (!window.confirm("Rigenerare i solleciti del mese? Verranno rifiutati quelli ancora in coda, sbloccati i clienti a cui il sollecito non è mai partito e rigenerati i solleciti corretti (solo F24 con scadenza già passata).")) return;
                      const r = await api.post("/f24/reminder/rigenera");
                      window.alert(`Fatto: ${r.data.rifiutate_in_coda} vecchi solleciti rifiutati · ${r.data.sbloccati} F24 sbloccati (${r.data.protetti} protetti) · ${r.data.sent} nuovi solleciti in coda Da approvare (${r.data.skipped} clienti saltati).`);
                    }}>♻️ Rigenera solleciti (pulisci e rifai)</button>
            <button className="btn sm" data-testid="f24-reminder-unlock" disabled={busy}
                    title="Da usare se hai rifiutato dei solleciti e vuoi rigenerarli: azzera il blocco «già sollecitato questo mese» per i soli clienti a cui il sollecito non è mai davvero partito"
                    onClick={async () => {
                      if (!window.confirm("Sbloccare il mese? Verranno resi di nuovo sollecitabili solo i clienti il cui sollecito NON è mai partito (rifiutato o rimasto in coda). Chi l'ha già ricevuto resta protetto.")) return;
                      const r = await api.post("/f24/reminder/unlock-month");
                      window.alert(`Sbloccati: ${r.data.sbloccati} F24 · Protetti (sollecito già ricevuto): ${r.data.protetti}. Ora premi "Invia solleciti ora".`);
                    }}>🔓 Sblocca il mese</button>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn" onClick={onClose}>Chiudi</button>
            <button className="btn primary" onClick={save} disabled={busy} data-testid="f24-reminder-save">{busy ? "…" : "Salva"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReminderPreviewModal({ record, onClose, onSent }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.post("/f24/reminder/preview", { record_id: record.id })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, [record.id]);

  const send = async () => {
    if (!window.confirm(`Preparare il sollecito F24 per ${data.to}? Finirà nella coda "Da approvare": lo invii tu dopo averlo controllato.`)) return;
    setBusy(true); setErr("");
    try {
      const r = await api.post("/f24/reminder/send", { record_id: record.id });
      onSent(r.data.queued
        ? `Sollecito per ${r.data.to} messo in "Da approvare" (${r.data.lines} righe). Vai su Da approvare per inviarlo.`
        : `Sollecito inviato a ${r.data.to} (${r.data.lines} righe).`);
    } catch (e) { setErr(e.response?.data?.detail || e.message); setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 760 }} onClick={(e) => e.stopPropagation()} data-testid="f24-reminder-preview-modal">
        <h3>Sollecito F24 — {record.client_name}</h3>
        {err && <p style={{ color: "#fb7185" }} data-testid="f24-preview-err">{err}</p>}
        {!data ? <p className="muted">Preparo l'anteprima…</p> : (
          <>
            <p className="muted" style={{ fontSize: 13 }}>A: <strong>{data.to}</strong> · Posizioni aperte: <strong>{data.lines}</strong> · Totale: <strong>{eur(data.total)}</strong></p>
            <p className="muted" style={{ fontSize: 13 }}>Oggetto: {data.subject}</p>
            <div style={{ background: "#fff", color: "#1f2937", padding: 4, borderRadius: 10, maxHeight: 360, overflow: "auto" }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.body_html) }} />
          </>
        )}
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Chiudi</button>
          <button className="btn primary" onClick={send} disabled={busy || !data || data.lines === 0} data-testid="f24-preview-send">{busy ? "Invio…" : "Invia sollecito"}</button>
        </div>
      </div>
    </div>
  );
}

function RecurringModal({ companies, onClose, onChanged }) {
  const [list, setList] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [tributo, setTributo] = useState("iva_mensile");

  const load = () => api.get("/f24/recurring").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!companyId) return;
    const c = companies.find((x) => x.id === companyId);
    await api.post("/f24/recurring", { company_id: companyId, client_name: c ? c.name : "", tributo });
    setCompanyId(""); load(); onChanged && onChanged();
  };
  const del = async (id) => { await api.delete(`/f24/recurring/${id}`); load(); onChanged && onChanged(); };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 620 }} onClick={(e) => e.stopPropagation()} data-testid="f24-recurring-modal">
        <h3>👥 Clienti ricorrenti (importo obbligatorio ogni mese)</h3>
        <p className="muted" style={{ marginTop: -6 }}>Esempio: clienti a cui addebiti tu l'F24 delle paghe. Se in un mese manca l'importo, te lo segnalo nel riquadro arancione così puoi recuperarlo.</p>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div className="field" style={{ flex: 1, minWidth: 220, margin: 0 }}><label>Cliente</label>
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="recurring-company">
              <option value="">— scegli —</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="field" style={{ margin: 0 }}><label>Casella obbligatoria</label>
            <select value={tributo} onChange={(e) => setTributo(e.target.value)} data-testid="recurring-tributo">
              {TRIBUTI.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <button className="btn primary" onClick={add} disabled={!companyId} data-testid="recurring-add">Aggiungi</button>
        </div>
        <table style={{ marginTop: 14 }}>
          <thead><tr><th>Cliente</th><th>Casella</th><th></th></tr></thead>
          <tbody>
            {list.map((x) => (
              <tr key={x.id} data-testid={`recurring-row-${x.id}`}>
                <td>{x.client_name}</td><td>{x.tributo_label}</td>
                <td><button className="btn sm danger" onClick={() => del(x.id)} data-testid={`recurring-del-${x.id}`}>Rimuovi</button></td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={3} className="muted">Nessun cliente ricorrente impostato.</td></tr>}
          </tbody>
        </table>
        <div className="modal-actions"><button className="btn" onClick={onClose}>Chiudi</button></div>
      </div>
    </div>
  );
}
